import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "../../hooks/useAuth";
import { useOrgSnapshot } from "../../hooks/useOrgSnapshot";
import { Spinner } from "../shared/Spinner";
import { Onboarding, type OrgFiscal } from "../../lib/apiTyped";
import { useApiCall } from "../../lib/useToastError";
import { StepName } from "./steps/StepName";
import { StepDesignPartner } from "./steps/StepDesignPartner";
import { StepFiscal, type FiscalDraft } from "./steps/StepFiscal";
import { StepPlans } from "./steps/StepPlans";
import { StepLogo } from "./steps/StepLogo";
import { StepDone } from "./steps/StepDone";

export type Step = "name" | "designPartner" | "fiscal" | "plans" | "logo" | "done";
export type OnboardingMode = "owner" | "member";

const OWNER_STEP_ORDER: readonly Step[] = [
  "name",
  "designPartner",
  "fiscal",
  "plans",
  "logo",
  "done",
] as const;

// Members don't own the org and don't fill fiscal/plans — we only ask for
// their display name and drop them at the dashboard where the tour kicks in.
const MEMBER_STEP_ORDER: readonly Step[] = ["name", "done"] as const;

const dpSeenKey = (orgId: string) => `formular:onboarding-dp-seen:${orgId}`;

const transition = { duration: 0.32, ease: [0.22, 1, 0.36, 1] as const };

const variants = {
  enter: { x: 40, opacity: 0 },
  center: { x: 0, opacity: 1 },
  exit: { x: -40, opacity: 0 },
};

const reducedMotionVariants = {
  enter: { opacity: 0 },
  center: { opacity: 1 },
  exit: { opacity: 0 },
};

function emailPrefix(email: string | null | undefined) {
  if (!email) return "";
  return (email.split("@")[0] ?? "").replace(/[^A-Za-z0-9._-]/g, "").slice(0, 80);
}

function fiscalDraftFromOrg(
  existing: OrgFiscal | null | undefined,
  fallbackEmail: string,
): FiscalDraft {
  return {
    pais: existing?.pais ?? "ES",
    razonSocial: existing?.razonSocial ?? "",
    nif: existing?.nif ?? "",
    taxIdType: existing?.taxIdType,
    line1: existing?.direccionFiscal?.line1 ?? "",
    line2: existing?.direccionFiscal?.line2 ?? "",
    city: existing?.direccionFiscal?.city ?? "",
    postalCode: existing?.direccionFiscal?.postalCode ?? "",
    region: existing?.direccionFiscal?.region ?? "",
    emailFacturacion: existing?.emailFacturacion ?? fallbackEmail,
    consent: false,
  };
}

export function OnboardingFlow({ mode = "owner" }: { mode?: OnboardingMode }) {
  const { user, profile } = useAuth();
  const { orgId, org, loading } = useOrgSnapshot();
  const navigate = useNavigate();
  const { run } = useApiCall();
  const reduceMotion = useReducedMotion();
  const [skipping, setSkipping] = useState(false);

  const orgRecord = (org ?? null) as
    | (Record<string, unknown> & {
        designPartner?: boolean;
        fiscal?: OrgFiscal | null;
        fiscalConsent?: unknown;
      })
    | null;

  const designPartner = orgRecord?.designPartner === true;
  const consentRecorded = Boolean(orgRecord?.fiscalConsent);
  const fiscal = orgRecord?.fiscal ?? null;

  // Always start at "name". Even when Google handed us a sensible display
  // name we still want the user to confirm/edit it explicitly so the
  // dashboard greeting is intentional, not whatever Google had on file.
  const initialStep: Step = "name";
  void designPartner;
  void fiscal;
  void orgId;

  const [step, setStep] = useState<Step>(initialStep);

  const [fiscalDraft, setFiscalDraft] = useState<FiscalDraft>(() =>
    fiscalDraftFromOrg(fiscal, user?.email ?? ""),
  );

  // Hydrate fiscalDraft once when org loads (handles both new orgs without
  // fiscal and existing orgs with fiscal).
  useEffect(() => {
    if (loading) return;
    setFiscalDraft((prev) => {
      // Don't clobber edits in progress: only hydrate if everything is empty.
      const isEmpty =
        !prev.razonSocial && !prev.nif && !prev.line1 && !prev.city;
      if (!isEmpty) return prev;
      return fiscalDraftFromOrg(fiscal, user?.email ?? "");
    });
  }, [loading, fiscal, user?.email]);

  const baseOrder = mode === "member" ? MEMBER_STEP_ORDER : OWNER_STEP_ORDER;
  const visibleSteps: Step[] = useMemo(
    () => baseOrder.filter((s) => s !== "designPartner" || designPartner),
    [baseOrder, designPartner],
  );

  // Owners can skip the org-level onboarding at any point past "name". The
  // org doc gets onboardingCompleted=true even though fiscal/plans are
  // empty — Billing / Subscription surface their own empty-states + the
  // OwnerAlerts banner prompts the user to come back and finish.
  // Members don't get a skip: their flow is just name, and skipping nothing
  // is silly.
  async function skipOnboarding() {
    if (skipping) return;
    if (mode !== "owner" || !orgId) return;
    setSkipping(true);
    const ok = await run(() => Onboarding.complete(orgId));
    setSkipping(false);
    if (ok) navigate("/", { replace: true });
  }

  // Plans is the one step we force a decision on: skipping past it would
  // leave the org with no tier and silently bypass the "request paid plan"
  // banner, defeating the whole point of the redesigned step. Owners who
  // genuinely don't want to pick now can still bail by closing the tab —
  // they'll land here again next session.
  const canSkip =
    mode === "owner" &&
    step !== "name" &&
    step !== "plans" &&
    step !== "done" &&
    !!orgId;

  function goNext() {
    const idx = visibleSteps.indexOf(step);
    if (idx < 0 || idx >= visibleSteps.length - 1) return;
    setStep(visibleSteps[idx + 1]);
  }

  function goBack() {
    const idx = visibleSteps.indexOf(step);
    if (idx <= 0) return;
    setStep(visibleSteps[idx - 1]);
  }

  if (loading || !orgId) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  const currentIdx = visibleSteps.indexOf(step);
  const motionVariants = reduceMotion ? reducedMotionVariants : variants;

  return (
    <div className="min-h-screen bg-bg flex flex-col">
      <header className="flex items-center justify-between px-6 sm:px-10 py-6">
        <a
          href="https://formular.so"
          target="_blank"
          rel="noreferrer noopener"
          className="text-ink hover:opacity-70 transition-opacity"
        >
          <span className="text-sm font-black tracking-tight">Formular</span>
        </a>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2" aria-label="Progreso">
            {visibleSteps.map((s, i) => (
              <span
                key={s}
                className="h-1.5 rounded-full transition-all duration-300"
                style={{
                  width: i === currentIdx ? 28 : 8,
                  backgroundColor: i <= currentIdx ? "#0F0F0F" : "#E8E4DF",
                }}
              />
            ))}
          </div>
          {canSkip && (
            <button
              type="button"
              onClick={skipOnboarding}
              disabled={skipping}
              className="text-xs font-semibold text-ink-3 hover:text-ink transition-colors disabled:opacity-40 cursor-pointer"
            >
              {skipping ? "Saltando…" : "Saltar onboarding"}
            </button>
          )}
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 sm:px-10 pb-24">
        <div className="w-full max-w-3xl">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={step}
              variants={motionVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={transition}
            >
              {step === "name" && (
                <StepName
                  initialName={profile?.display_name ?? ""}
                  fallbackPlaceholder={emailPrefix(user?.email)}
                  onContinue={goNext}
                />
              )}
              {step === "designPartner" && orgId && (
                <StepDesignPartner
                  orgId={orgId}
                  onContinue={() => {
                    sessionStorage.setItem(dpSeenKey(orgId), "1");
                    goNext();
                  }}
                  onBack={goBack}
                />
              )}
              {step === "fiscal" && orgId && (
                <StepFiscal
                  orgId={orgId}
                  draft={fiscalDraft}
                  onDraftChange={setFiscalDraft}
                  consentRecorded={consentRecorded}
                  onSaved={goNext}
                  onBack={goBack}
                  onSkip={goNext}
                />
              )}
              {step === "plans" && (
                <StepPlans
                  orgId={orgId}
                  orgName={(orgRecord?.name as string) ?? ""}
                  email={user?.email ?? ""}
                  onContinue={goNext}
                  onBack={goBack}
                />
              )}
              {step === "logo" && orgId && (
                <StepLogo
                  orgId={orgId}
                  initialLogoUrl={
                    (orgRecord as { logoUrl?: string | null } | null)?.logoUrl ??
                    null
                  }
                  onContinue={goNext}
                  onBack={goBack}
                />
              )}
              {step === "done" && (
                <StepDone
                  mode={mode}
                  orgId={orgId}
                  displayName={profile?.display_name ?? ""}
                  designPartner={designPartner}
                  onBack={goBack}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}

function useReducedMotion() {
  const [prefers, setPrefers] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefers(mq.matches);
    const handler = (e: MediaQueryListEvent) => setPrefers(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return prefers;
}
